"use client";

import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useT } from "@/lib/i18n/context";
import type { WClientTestimonialsContent } from "@/lib/api/cms-window-content";
import { wClientTestimonialsTranslations } from "./WClientTestimonials.i18n";
import styles from "./WClientTestimonials.module.css";

/**
 * WClientTestimonials - okno "Opinie klientow".
 *
 * Carousel referencji (`c_dedicated_realizations.client_about_realization`) losowo pobranych
 * z calej domeny+jezyka. Swiper z auto-rotate (6s) + navigation arrows + clickable
 * pagination dots. 1 slide na raz.
 *
 * Struktura slide'a:
 *   - Duza zielona ikona cytatu (::before pseudoelement)
 *   - Testimonial text (HTML z TinyMCE, dangerouslySetInnerHTML)
 *   - Autor: awatar z inicjalami (fallback gdy brak logo) + nazwa klienta + sektor
 */
export function WClientTestimonials({ content, title }: WClientTestimonialsProps) {
  const t = useT(wClientTestimonialsTranslations);

  const items = useMemo(() => content?.items ?? [], [content]);

  if (!content || items.length === 0) return null;

  const heading = title || t.defaultTitle;
  const eyebrow = (content.hero_text ?? "").trim();

  return (
    <section className={styles.root}>
      {eyebrow !== "" ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
      {heading ? <h2 className={styles.heading}>{heading}</h2> : null}

      <Swiper
        modules={[Autoplay, Pagination, Navigation, A11y]}
        slidesPerView={1}
        loop={items.length > 1}
        autoplay={items.length > 1 ? { delay: 6000, disableOnInteraction: false } : false}
        pagination={items.length > 1 ? { clickable: true, el: `.${styles.pagination}` } : false}
        navigation={
          items.length > 1
            ? { prevEl: `.${styles.prevBtn}`, nextEl: `.${styles.nextBtn}` }
            : false
        }
        a11y={{ enabled: true, prevSlideMessage: t.prev, nextSlideMessage: t.next, paginationBulletMessage: t.goToSlide }}
        spaceBetween={0}
        className={styles.swiper}
      >
        {items.map((item) => {
          const initials = getInitials(item.client_name);
          return (
            <SwiperSlide key={item.id} className={styles.slide}>
              <div
                className={styles.testimonial}
                dangerouslySetInnerHTML={{ __html: item.testimonial_html }}
              />
              <div className={styles.author}>
                <span className={styles.avatar} aria-hidden="true">
                  <span className={styles.avatarInitials}>{initials}</span>
                </span>
                <span className={styles.authorMeta}>
                  <span className={styles.authorName}>{item.client_name}</span>
                  {item.client_sector ? (
                    <span className={styles.authorSector}>{item.client_sector}</span>
                  ) : null}
                </span>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {items.length > 1 ? (
        <div className={styles.controls}>
          <button type="button" className={styles.prevBtn} aria-label={t.prev} />
          <div className={styles.pagination} />
          <button type="button" className={styles.nextBtn} aria-label={t.next} />
        </div>
      ) : null}
    </section>
  );
}

/** Pierwsze 2 znaki (litery/cyfry) z nazwy klienta - fallback gdy brak logo. */
function getInitials(name: string): string {
  const trimmed = (name ?? "").trim();
  if (trimmed === "") return "?";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

export type WClientTestimonialsProps = {
  content: WClientTestimonialsContent | null;
  title: string;
};

export default WClientTestimonials;
